import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { MoneyCell } from '../../components/MoneyCell/MoneyCell';
import { NumberCell } from '../../components/NumberCell/NumberCell';
import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { ColumnViewInfo } from '../../typings/common';
import { DocCenterColumnTooltip } from '../../typings/doc-center';
import { Domain } from '../../typings/domain';
import { CurrencyCode } from '../../typings/graphql-grid';
import { GridCellProps, GridColumnsOptions, GridScreenTotalKey, OptionalGridColumnDecl } from '../../typings/grid';

import * as keyset from './stats.i18n';

const statsI18N = i18nFactory(keyset);

export interface DataGetters<T extends Domain> {
    getCurrency: (item: T) => CurrencyCode;
}

export enum StatKey {
    STAT_COST = 'STAT_COST',
    STAT_SHOWS = 'STAT_SHOWS',
    STAT_CLICKS = 'STAT_CLICKS',
    STAT_CTR = 'STAT_CTR',
    STAT_REVENUE = 'STAT_REVENUE',
    STAT_GOALS = 'STAT_GOALS',
    STAT_PROFITABILITY = 'STAT_PROFITABILITY',
    STAT_AVG_GOAL_COST = 'STAT_AVG_GOAL_COST',
    STAT_AVG_CLICK_COST = 'STAT_AVG_CLICK_COST',
    STAT_CPM_PRICE = 'STAT_CPM_PRICE',
    STAT_BOUNCE_RATE = 'STAT_BOUNCE_RATE',
    STAT_CONVERSION_RATE = 'STAT_CONVERSION_RATE'
}

export function getStaticStatsColumns<T extends Domain>(
    getters: DataGetters<T>,
    clientCurrency: CurrencyCode,
    isCompactViewEnabled: boolean,
    hasSelectedGoals: boolean,
    isGoalsStatEnabled?: boolean,
    hasSelectedGoalsAtColumnsSettingsEditor?: boolean,
    isColumnsSettingsEditorLocation?: boolean
): Array<OptionalGridColumnDecl<T, {}, GridColumnsOptions>> {
    const currency = mapStringToCurrencySymbol(clientCurrency);
    const isShowStatForGroupedByGoals =
        (isColumnsSettingsEditorLocation && !hasSelectedGoalsAtColumnsSettingsEditor) ||
        (!isColumnsSettingsEditorLocation && !(isGoalsStatEnabled && hasSelectedGoals));

    return [
        // колонки со статистикой
        {
            title: statsI18N('cost-with-currency', { currency }),
            titleSettings: statsI18N('cost'),
            key: StatKey.STAT_COST,
            align: 'right',
            tooltipKey: DocCenterColumnTooltip.cost,
            minWidth: 90,
            maxWidth: 200,
            CellComponent: React.memo(
                ({ rowData: item }: GridCellProps<T>): JSX.Element => {
                    return <MoneyCell num={item.stats.cost} />;
                }
            )
        },
        {
            title: statsI18N('goals'),
            key: StatKey.STAT_GOALS,
            align: 'right',
            tooltipKey: DocCenterColumnTooltip.goals,
            minWidth: 100,
            maxWidth: 150,
            isAvailable: (): boolean => {
                return isShowStatForGroupedByGoals;
            },
            CellComponent: React.memo(
                ({ rowData: item }: GridCellProps<T>): JSX.Element => {
                    return <NumberCell num={item.stats.goals} />;
                }
            )
        },
        {
            title: statsI18N('revenue-with-currency', { currency }),
            titleSettings: statsI18N('revenue'),
            key: StatKey.STAT_REVENUE,
            align: 'right',
            tooltipKey: DocCenterColumnTooltip.revenue,
            minWidth: 90,
            maxWidth: 200,
            isAvailable: (options: GridColumnsOptions): boolean => {
                return options.hasEcommerce;
            },
            CellComponent: React.memo(
                ({ rowData: item }: GridCellProps<T>): JSX.Element => {
                    return <MoneyCell num={item.stats.revenue} />;
                }
            )
        },
        {
            title: statsI18N('profitability-words'),
            key: StatKey.STAT_PROFITABILITY,
            align: 'right',
            tooltipKey: DocCenterColumnTooltip.profitability,
            minWidth: 225,
            maxWidth: 225,
            isAvailable: (options: GridColumnsOptions): boolean => {
                return options.hasEcommerce;
            },
            CellComponent: React.memo(
                ({ rowData: item }: GridCellProps<T>): JSX.Element => {
                    return <NumberCell num={item.stats.profitability} fractionalPartLength={2} />;
                }
            )
        },
        {
            title: statsI18N('avg-goal-cost-with-currency', { currency }),
            titleSettings: statsI18N('avg-goal-cost'),
            key: StatKey.STAT_AVG_GOAL_COST,
            align: 'right',
            tooltipKey: DocCenterColumnTooltip.avgGoalCost,
            minWidth: 165,
            maxWidth: 165,
            isAvailable: (options: GridColumnsOptions): boolean => {
                return options.hasCampaignsWithCPAStrategy && isShowStatForGroupedByGoals;
            },
            CellComponent: React.memo(
                ({ rowData: item }: GridCellProps<T>): JSX.Element => {
                    return <MoneyCell num={item.stats.avgGoalCost} />;
                }
            )
        },
        {
            title: statsI18N('avg-click-cost-with-currency', { currency }),
            titleSettings: statsI18N('avg-click-cost'),
            key: StatKey.STAT_AVG_CLICK_COST,
            align: 'right',
            tooltipKey: DocCenterColumnTooltip.avgClickCost,
            minWidth: 135,
            maxWidth: 135,
            isAvailable: (options: GridColumnsOptions): boolean => {
                return !options.hasOnlyCpmCampaigns;
            },
            CellComponent: React.memo(
                ({ rowData: item }: GridCellProps<T>): JSX.Element => {
                    return <MoneyCell num={item.stats.avgClickCost} />;
                }
            )
        },
        {
            title: statsI18N('shows'),
            key: StatKey.STAT_SHOWS,
            align: 'right',
            tooltipKey: DocCenterColumnTooltip.shows,
            minWidth: 45,
            maxWidth: 150,
            CellComponent: React.memo(
                ({ rowData: item }: GridCellProps<T>): JSX.Element => {
                    return <NumberCell num={item.stats.shows} />;
                }
            )
        },
        {
            title: statsI18N('avg-cpm-cost-with-currency', { currency }),
            titleSettings: statsI18N('avg-cpm-cost'),
            key: StatKey.STAT_CPM_PRICE,
            align: 'right',
            minWidth: isCompactViewEnabled ? 75 : 120,
            maxWidth: isCompactViewEnabled ? 75 : 175,
            tooltipKey: DocCenterColumnTooltip.avgCpmCost,
            isAvailable: (options: GridColumnsOptions): boolean => {
                return options.hasOnlyCpmCampaigns;
            },
            CellComponent: React.memo(
                ({ rowData: item }: GridCellProps<T>): JSX.Element => {
                    return <MoneyCell num={item.stats.avgCpmCost} />;
                }
            )
        },
        {
            title: statsI18N('clicks'),
            key: StatKey.STAT_CLICKS,
            align: 'right',
            tooltipKey: DocCenterColumnTooltip.clicks,
            minWidth: 75,
            maxWidth: 150,
            CellComponent: React.memo(
                ({ rowData: item }: GridCellProps<T>): JSX.Element => {
                    return <NumberCell num={item.stats.clicks} />;
                }
            )
        },
        {
            title: statsI18N('ctr-with-percent'),
            key: StatKey.STAT_CTR,
            align: 'right',
            tooltipKey: DocCenterColumnTooltip.ctr,
            minWidth: 70,
            maxWidth: 70,
            CellComponent: React.memo(
                ({ rowData: item }: GridCellProps<T>): JSX.Element => {
                    return <NumberCell num={item.stats.ctr} />;
                }
            )
        },
        {
            title: statsI18N('conversionRate'),
            key: StatKey.STAT_CONVERSION_RATE,
            align: 'right',
            tooltipKey: DocCenterColumnTooltip.conversionRate,
            minWidth: 115,
            maxWidth: 115,
            isAvailable: (options: GridColumnsOptions): boolean => {
                return options.hasMetrikaCounters && isShowStatForGroupedByGoals;
            },
            CellComponent: React.memo(
                ({ rowData: item }: GridCellProps<T>): JSX.Element => {
                    return <NumberCell num={item.stats.conversionRate} fractionalPartLength={2} />;
                }
            )
        },
        {
            title: statsI18N('bounce-rate-with-percent'),
            key: StatKey.STAT_BOUNCE_RATE,
            align: 'right',
            tooltipKey: DocCenterColumnTooltip.bounceRate,
            minWidth: 95,
            maxWidth: 95,
            isAvailable: (options: GridColumnsOptions): boolean => {
                return options.hasMetrikaCounters;
            },
            CellComponent: React.memo(
                ({ rowData: item }: GridCellProps<T>): JSX.Element => {
                    return <NumberCell num={item.stats.bounceRate} />;
                }
            )
        }
    ];
}

export function getDynamicStatsColumnsFields(): ColumnViewInfo {
    return {
        [StatKey.STAT_COST]: {
            width: 90,
            widthCompact: 90,
            hidden: false,
            totalKey: GridScreenTotalKey.cost
        },
        [StatKey.STAT_GOALS]: {
            width: 100,
            widthCompact: 100,
            hidden: false
        },
        [StatKey.STAT_REVENUE]: {
            width: 120,
            widthCompact: 120,
            hidden: false
        },
        [StatKey.STAT_PROFITABILITY]: {
            width: 225,
            widthCompact: 225,
            hidden: false
        },
        [StatKey.STAT_AVG_GOAL_COST]: {
            width: 165,
            widthCompact: 165,
            hidden: false
        },
        [StatKey.STAT_AVG_CLICK_COST]: {
            width: 135,
            widthCompact: 135,
            hidden: false
        },
        [StatKey.STAT_SHOWS]: {
            width: 100,
            widthCompact: 100,
            hidden: false,
            totalKey: GridScreenTotalKey.shows
        },
        [StatKey.STAT_CPM_PRICE]: {
            width: 175,
            widthCompact: 175,
            hidden: false
        },
        [StatKey.STAT_CLICKS]: {
            width: 90,
            widthCompact: 90,
            hidden: false,
            totalKey: GridScreenTotalKey.clicks
        },
        [StatKey.STAT_CTR]: {
            width: 70,
            widthCompact: 70,
            hidden: false,
            totalKey: GridScreenTotalKey.ctr
        },
        [StatKey.STAT_CONVERSION_RATE]: {
            width: 115,
            widthCompact: 115,
            hidden: false
        },
        [StatKey.STAT_BOUNCE_RATE]: {
            width: 95,
            widthCompact: 95,
            hidden: false
        }
    };
}
